import { useState, useEffect } from 'react';
import { FiPlus, FiTrash2, FiToggleLeft, FiToggleRight, FiEdit2 } from 'react-icons/fi';
import {
  apiGetAllBanners,
  apiDeleteBanner,
  apiToggleBanner,
} from '../api';
import { BannerForm } from './banners/BannerForm';
import { resolveApiAsset, handleAdminAssetError } from '../config/urls';
import { confirmAdminAction } from '../utils/adminConfirm';

const displayBannerTitle = (title) =>
  /^(hero|store|promo|offer) banner \d+$/i.test(String(title || '').trim()) ? '' : title;

const BannersManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const data = await apiGetAllBanners();
      setBanners(data.banners || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggle = async (id) => {
    try {
      const data = await apiToggleBanner(id);
      setBanners(
        banners.map((b) =>
          b.id === id ? { ...b, isActive: data.banner?.isActive ?? !b.isActive } : b
        )
      );
    } catch { /* API client displays the error toast. */ }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAdminAction('This banner will be permanently removed.', {
      title: 'Delete banner', confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    try {
      await apiDeleteBanner(id);
      setBanners(banners.filter((b) => b.id !== id));
    } catch { /* API client displays the error toast. */ }
  };

  const handleSaved = () => {
    setShowForm(false);
    setEditing(null);
    load();
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading banners...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-black">{banners.length} banners</p>
        <button
          onClick={() => {
            setEditing(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-700 transition"
        >
          <FiPlus size={16} /> Add Banner
        </button>
      </div>

      {/* Banner Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {banners.map((banner) => (
          <div
            key={banner.id}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition"
          >
            {banner.image && (
              <img
                src={resolveApiAsset(banner.image)}
                onError={handleAdminAssetError}
                alt={displayBannerTitle(banner.title) || `${banner.type || 'hero'} banner`}
                className="w-full h-40 object-cover"
              />
            )}
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-black truncate">
                  {displayBannerTitle(banner.title) || 'Image-only banner'}
                </h3>
                <span
                  className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
                    banner.type === 'store'
                      ? 'bg-purple-100 text-purple-700'
                      : banner.type === 'offer'
                        ? 'bg-red-100 text-red-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {banner.type || 'hero'}
                </span>
              </div>
              <p className="text-sm text-black mt-1 line-clamp-2">{banner.subtitle}</p>
              {banner.bgColor && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-black">BG:</span>
                  <span className={`w-5 h-5 rounded ${banner.bgColor}`}></span>
                  <span className="text-xs text-black">{banner.bgColor}</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                <span
                  className={`text-xs font-medium px-2 py-1 rounded-full ${
                    banner.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-black'
                  }`}
                >
                  {banner.isActive ? 'Active' : 'Inactive'}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggle(banner.id)}
                    className="text-black hover:text-orange-600"
                    title="Toggle Active"
                  >
                    {banner.isActive ? (
                      <FiToggleRight size={20} className="text-green-600" />
                    ) : (
                      <FiToggleLeft size={20} />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setEditing(banner);
                      setShowForm(true);
                    }}
                    className="text-blue-500 hover:text-blue-700"
                    title="Edit"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(banner.id)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {banners.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <p className="text-black">No banners created yet</p>
        </div>
      )}

      {showForm && (
        <BannerForm
          banner={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default BannersManager;
